import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { alpha, useTheme } from "@mui/material/styles";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";

const API_URL = process.env.REACT_APP_API_URL;
const meses = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

const PygTable = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMinimized } = useContext(SidebarContext);
  const marginLeft = isMinimized ? "3em" : "13.5em";
  const [estructura, setEstructura] = useState([]);
  const [accData, setAccData] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());

  // Validación del token
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch (err) {
      console.error("Token error:", err);
      navigate("/login");
    }
  }, [navigate]);

  // Cargar estructura visual
  useEffect(() => {
    fetch(`${API_URL}/api/pyg/estructura`)
      .then((res) => res.json())
      .then(setEstructura)
      .catch((err) => console.error("Error cargando estructura:", err));
  }, []);

  // Cargar datos reales (ACC)
  useEffect(() => {
    fetch(`${API_URL}/api/pyg/acc?year=${year}`)
      .then((res) => res.json())
      .then(setAccData)
      .catch((err) => console.error("Error cargando ACC:", err));
  }, [year]);

  // Construir datos finales
  /*   const dataFinal = useMemo(() => {
    return estructura.map((linea) => {
      const valoresMes = {};
      let total = 0;

      meses.forEach((mes, index) => {
        const mesIndex = index + 1;
        const dato = accData.find(
          (d) => d.codigo === linea.codigo && d.mes === mesIndex
        );
        const valor = dato ? parseFloat(dato.valor) : 0;
        valoresMes[mes] = valor;
        total += valor;
      });

      return {
        ...linea,
        ...valoresMes,
        Total: total,
      };
    });
  }, [estructura, accData]); */

  const dataFinal = useMemo(() => {
    const enriched = estructura.map((linea, idx) => {
      const valoresMes = {};
      let total = 0;

      meses.forEach((mes, index) => {
        const mesIndex = index + 1;
        const dato = accData.find(
          (d) => d.codigo === linea.codigo && d.mes === mesIndex,
        );
        const valor = dato ? parseFloat(dato.valor) : 0;
        valoresMes[mes] = valor;
        total += valor;
      });

      return {
        ...linea,
        ...valoresMes,
        Total: total,
        idx,
      };
    });

    // 🧮 Sumar manualmente cuentas para "a) Ventas."
    const normalizar = (texto) =>
      texto?.toString().trim().toLowerCase().replace(/\s+/g, " ") || "";

    const cuentasVentas = [
      "70000000",
      "70000004",
      "70000005",
      "70000006",
      "70000007",
      "70800000",
    ];

    const idxVentas = enriched.findIndex(
      (l) => normalizar(l.cuenta) === "a) ventas.",
    );

    if (idxVentas !== -1) {
      const ventasLine = enriched[idxVentas];
      let total = 0;

      meses.forEach((mes) => {
        const sumaMes = enriched
          .filter((l) => cuentasVentas.includes(l.codigo))
          .reduce((acc, l) => acc + (l[mes] || 0), 0);

        ventasLine[mes] = sumaMes;
        total += sumaMes;
      });

      ventasLine.Total = total;
    } else {
      console.warn("❌ No se encontró 'a) Ventas.' en enriched con .cuenta");
    }
    /*  */
    return enriched;
  }, [estructura, accData]);

  return (
    <Box sx={{ p: { xs: 1.25, sm: 2 }, marginLeft }}>
      <Box
        sx={{
          p: { xs: 1.25, sm: 1.75 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.8),
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
              : "linear-gradient(135deg, #eef9fb 0%, #f4f0ff 60%, #f0faf4 100%)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 8px 24px rgba(0,0,0,0.35)"
              : "0 8px 24px rgba(15,23,42,0.08)",
          marginBottom: 1.5,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ lineHeight: 1.1, letterSpacing: 1.1, fontWeight: 700 }}
            >
              FINANCE · PYG ANALITICO
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              Perdidas y Ganancias {year}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.6 }} useFlexGap>
              <Chip
                size="small"
                icon={<AutoGraphOutlinedIcon sx={{ fontSize: 16 }} />}
                label={`Ano ${year}`}
                sx={{ fontWeight: 700 }}
              />
              <Chip size="small" variant="outlined" label="Analitico" sx={{ fontWeight: 700 }} />
            </Stack>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap>
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 130 } }}>
              <InputLabel>Ano</InputLabel>
              <Select
                value={year}
                label="Ano"
                onChange={(e) => setYear(e.target.value)}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.92),
                  borderRadius: 1.5,
                }}
              >
                {[2023, 2024, 2025, 2026].map((año) => (
                  <MenuItem key={año} value={año}>
                    {año}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              startIcon={<OpenInNewOutlinedIcon />}
              onClick={() => navigate("/pygdebug")}
              sx={{ fontWeight: 700, borderRadius: 1.5, whiteSpace: "nowrap" }}
            >
              Vista Debug
            </Button>
          </Stack>
        </Stack>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "87vh",
          overflowY: "auto",
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.8),
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 10px 24px rgba(0,0,0,0.34)"
              : "0 10px 24px rgba(15,23,42,0.08)",
          backgroundColor: alpha(theme.palette.background.paper, 0.97),
        }}
      >
        <Table
          size="small"
          sx={{
            "& .MuiTableCell-root": {
              py: 0.65,
              borderBottomColor: alpha(theme.palette.divider, 0.6),
            },
          }}
        >
          <TableHead
            sx={{
              "& .MuiTableCell-root": {
                background: alpha(theme.palette.background.paper, 0.98),
                backdropFilter: "blur(4px)",
                fontWeight: 800,
                letterSpacing: 0.2,
              },
            }}
          >
            <TableRow>
              <TableCell
                style={{
                  position: "sticky",
                  top: 0,
                    background: alpha(theme.palette.background.paper, 0.98),
                  zIndex: 1,
                }}
              >
                <strong>Título</strong>
              </TableCell>
              {meses.map((mes) => (
                <TableCell
                  key={mes}
                  align="right"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: alpha(theme.palette.background.paper, 0.98),
                    zIndex: 1,
                  }}
                >
                  <strong>{mes}</strong>
                </TableCell>
              ))}
              <TableCell
                align="right"
                style={{
                  position: "sticky",
                  top: 0,
                  background: alpha(theme.palette.background.paper, 0.98),
                  zIndex: 1,
                }}
              >
                <strong>Total</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataFinal.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell
                  style={{
                    paddingLeft:
                      row.indenta === 1 ? "1em" : `${(row.indenta - 1) * 15}px`,
                    fontWeight: row.indenta <= 2 ? "bold" : "normal",
                    fontSize: "12px",
                  }}
                >
                  {row.titulo || row.codigo}
                </TableCell>
                {meses.map((mes) => (
                  <TableCell
                    key={mes}
                    align="right"
                    style={{
                      color:
                        row[mes] === 0 ||
                        row[mes] === null ||
                        row[mes] === undefined
                          ? "white"
                          : row[mes] >= 0
                            ? "rgb(76, 98, 100)"
                            : "red",
                      fontWeight: 500,
                      fontSize: "12px",
                    }}
                  >
                    {(row[mes] || 0).toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                      useGrouping: true,
                    })}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  style={{
                    fontWeight: "bold",
                    color: (row.Total || 0) >= 0 ? "black" : "red",
                  }}
                >
                  {(row.Total || 0).toLocaleString("es-ES", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    useGrouping: true,
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PygTable;
